import { z } from "zod";
import { ClientLinkIdSchema } from "../primitives/ids.js";
import { ClientLinkStatusSchema } from "./client-link.js";
export const PreviewViewV1Schema = z
    .object({
    schemaId: z.literal("client-portal.previewView.v1"),
    clientLinkId: ClientLinkIdSchema,
    status: ClientLinkStatusSchema,
    displayName: z.string().min(1),
    previewStatus: z.literal("READY"),
    previewVersionId: z.string().length(64),
    previewRoute: z.string().min(1).default("/"),
    /** Route paths available in the rendered site package (multi-page preview). */
    availableRoutes: z.array(z.string().min(1)).min(1).optional(),
    previewHtml: z.string().min(1),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ApprovalSubmissionV1Schema = z
    .object({
    schemaId: z.literal("approval-submission.v1"),
    previewVersionId: z.string().length(64),
    approvedByName: z.string().min(1).max(120).optional(),
    note: z.string().max(500).optional(),
    /** QA-011: explicit client consent to approve this preview reference. */
    consentAcknowledged: z.literal(true),
})
    .strict();
export const ApprovalRecordV1Schema = z
    .object({
    schemaId: z.literal("approval-record.v1"),
    approvalId: z.string().min(1),
    clientLinkId: ClientLinkIdSchema,
    status: z.literal("APPROVED"),
    approvedAt: z.string().datetime(),
    approvedByName: z.string().min(1).max(120).nullable(),
})
    .strict();
export const InternalApprovalArtifactContextV1Schema = z
    .object({
    schemaId: z.literal("internal.approvalArtifactContext.v1"),
    runId: z.string().min(1),
    businessGraphRevisionId: z.string().min(1),
    artifactSetHash: z.string().min(1),
    exportBundleHash: z.string().min(1),
    approvedArtifactUpdatedAt: z.string().datetime(),
})
    .strict();
/** Authoritative ClientChangeRequest lifecycle (ENR-UX-007). */
export const ChangeRequestStatusSchema = z.enum(["SUBMITTED", "IN_PROGRESS", "RESOLVED"]);
export const ChangeRequestCreateV1Schema = z
    .object({
    schemaId: z.literal("change-request.create.v1"),
    message: z.string().min(1).max(4000),
    requestedByName: z.string().min(1).max(120).optional(),
})
    .strict();
export const ChangeRequestV1Schema = z
    .object({
    schemaId: z.literal("change-request.v1"),
    changeRequestId: z.string().min(1),
    clientLinkId: ClientLinkIdSchema,
    status: z.literal("SUBMITTED"),
    message: z.string().min(1).max(4000),
    requestedByName: z.string().min(1).max(120).nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
/** Client-safe revised-preview identity only — no raw run/artifact hashes. */
export const ClientSafeRevisedPreviewResolutionV1Schema = z
    .object({
    schemaId: z.literal("change-request.clientSafeResolution.v1"),
    previewVersionId: z.string().length(64),
    resolvedAt: z.string().datetime(),
})
    .strict();
/**
 * Internal Agency resolution tuple (authorized Agency contracts only).
 * exportBundleHash allows "" — the canonical pre-export sentinel (null/undefined/"").
 * Do not invent a second sentinel; normalize with canonicalPreExportExportBundleHash before parse.
 */
export const InternalChangeRequestResolutionContextV1Schema = z
    .object({
    schemaId: z.literal("internal.changeRequestResolutionContext.v1"),
    runId: z.string().min(1),
    businessGraphRevisionId: z.string().min(1),
    artifactSetHash: z.string().min(1),
    exportBundleHash: z.string(),
    resolvedArtifactUpdatedAt: z.string().datetime(),
    resolvedAt: z.string().datetime(),
    previewVersionId: z.string().length(64),
})
    .strict();
export const AgencyChangeRequestTransitionRequestV1Schema = z.discriminatedUnion("targetStatus", [
    z
        .object({
        schemaId: z.literal("agency.changeRequest.transition.v1"),
        targetStatus: z.literal("IN_PROGRESS"),
    })
        .strict(),
    z
        .object({
        schemaId: z.literal("agency.changeRequest.transition.v1"),
        targetStatus: z.literal("RESOLVED"),
        previewVersionId: z.string().length(64),
    })
        .strict(),
]);
export const AgencyChangeRequestTransitionResultV1Schema = z
    .object({
    schemaId: z.literal("agency.changeRequest.transitionResult.v1"),
    changeRequestId: z.string().min(1),
    clientLinkId: ClientLinkIdSchema,
    status: ChangeRequestStatusSchema,
    startedAt: z.string().datetime().nullable(),
    resolvedAt: z.string().datetime().nullable(),
    clientSafeResolution: ClientSafeRevisedPreviewResolutionV1Schema.nullable(),
    internalResolution: InternalChangeRequestResolutionContextV1Schema.nullable(),
})
    .strict();
export const ClientChangeRequestTimelineEntryV1Schema = z
    .object({
    schemaId: z.literal("change-request.timeline-entry.v1"),
    changeRequestId: z.string().min(1),
    status: ChangeRequestStatusSchema,
    statusLabel: z.string().min(1),
    resolutionLabel: z.string().min(1),
    message: z.string().min(1).max(4000),
    requestedByName: z.string().min(1).max(120).nullable(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable().optional(),
    resolvedAt: z.string().datetime().nullable().optional(),
    resolvedPreviewVersionId: z.string().length(64).nullable().optional(),
})
    .strict();
export const ClientChangeRequestTimelineV1Schema = z
    .object({
    schemaId: z.literal("change-request.timeline.v1"),
    clientLinkId: ClientLinkIdSchema,
    entries: z.array(ClientChangeRequestTimelineEntryV1Schema),
})
    .strict();
export const ClientReviewChangeRequestV1Schema = z
    .object({
    changeRequestId: z.string().min(1),
    clientLinkId: ClientLinkIdSchema,
    status: ChangeRequestStatusSchema,
    messagePreview: z.string().max(240),
    requestedByName: z.string().min(1).max(120).nullable(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable().optional(),
    resolvedAt: z.string().datetime().nullable().optional(),
    /** Present only when stored binding matches current authoritative passing artifact. */
    resolvedPreviewVersionId: z.string().length(64).nullable().optional(),
    /** True only when stored RESOLVED binding equals current LatestPassingArtifact tuple. */
    resolutionMatchesCurrentPassingArtifact: z.boolean().optional(),
})
    .strict();
export const ClientReviewStatusV1Schema = z
    .object({
    schemaId: z.literal("studio.clientReviewStatus.v1"),
    projectId: z.string().min(1),
    approvalMatchesLatestPassingArtifact: z.boolean(),
    clientLink: z
        .object({
        clientLinkId: ClientLinkIdSchema,
        status: ClientLinkStatusSchema,
        updatedAt: z.string().datetime(),
        clientDisplayName: z.string().min(1).optional(),
    })
        .nullable(),
    latestApproval: z
        .object({
        approvalId: z.string().min(1),
        clientLinkId: ClientLinkIdSchema,
        status: z.literal("APPROVED"),
        approvedByName: z.string().min(1).max(120).nullable(),
        approvedAt: z.string().datetime(),
    })
        .strict()
        .nullable(),
    latestChangeRequest: ClientReviewChangeRequestV1Schema.nullable(),
    latestPreview: z
        .object({
        previewVersionId: z.string().length(64),
        previewStatus: z.literal("READY"),
        updatedAt: z.string().datetime(),
    })
        .nullable(),
})
    .strict();
//# sourceMappingURL=approval-change.js.map